import { useState } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { PageHeader } from "@/ui/PageHeader";
import { Card } from "@/ui/Card";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { SegmentedFilter, type FilterOption } from "@/ui/SegmentedFilter";
import { DataTable, type Column } from "@/ui/data-table/DataTable";
import { LoadingState } from "@/ui/feedback/states";
import { ApiFallbackBanner } from "@/ui/feedback/ApiFallbackBanner";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { formatDZD } from "@/lib/formatters";
import { useLabels } from "@/lib/labels";
import type { Expense, Payment, Task } from "@/types";
import { useArchivedExpenses } from "@/features/expenses/expenses.hooks";
import { useRestoreExpense } from "@/features/expenses/expenses.hooks";
import { useArchivedPayments } from "@/features/payments/payments.hooks";
import { useRestorePayment } from "@/features/payments/payments.hooks";
import { useArchivedTasks } from "@/features/tasks/tasks.hooks";
import { useRestoreTask } from "@/features/tasks/tasks.hooks";

type Tab = "expenses" | "payments" | "tasks";

export function ArchivesPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("expenses");

  const TABS: FilterOption<Tab>[] = [
    { value: "expenses", label: t("archives.tabs.expenses") },
    { value: "payments", label: t("archives.tabs.payments") },
    { value: "tasks", label: t("archives.tabs.tasks") },
  ];

  return (
    <PageContainer>
      <PageHeader title={t("archives.title")} subtitle="" />
      <div className="mb-3.5">
        <SegmentedFilter options={TABS} value={tab} onChange={setTab} />
      </div>
      {tab === "expenses" && <ExpensesArchive />}
      {tab === "payments" && <PaymentsArchive />}
      {tab === "tasks" && <TasksArchive />}
    </PageContainer>
  );
}

function ExpensesArchive() {
  const { t } = useTranslation();
  const { EXPENSE_CATEGORY } = useLabels();
  const { expenses, isLoading, isError, refetch } = useArchivedExpenses();
  const restore = useRestoreExpense();

  const onRestore = async (id: string) => {
    try {
      await restore.mutateAsync(id);
      toast.success(t("archives.restoreSuccess"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("archives.restoreFailed")));
    }
  };

  const columns: Column<Expense>[] = [
    { key: "description", header: t("expenses.columns.description"), cell: (e) => <span className="font-medium">{e.description}</span> },
    { key: "category", header: t("expenses.columns.category"), cell: (e) => <Badge>{EXPENSE_CATEGORY[e.category].label}</Badge> },
    { key: "amount", header: t("expenses.columns.amount"), align: "right", cell: (e) => <span className="mono">{formatDZD(e.amount)}</span> },
    { key: "date", header: t("expenses.columns.date"), align: "right", cell: (e) => <span className="text-text3">{e.date}</span> },
    {
      key: "restore",
      header: "",
      align: "right",
      cell: (e) => (
        <Button variant="ghost" size="xs" onClick={() => onRestore(e.id)} disabled={restore.isPending}>
          <RotateCcw className="h-3 w-3 mr-1" /> {t("archives.restore")}
        </Button>
      ),
    },
  ];

  return (
    <>
      {isError && <ApiFallbackBanner onRetry={() => refetch()} />}
      <Card>
        {isLoading ? (
          <LoadingState rows={4} />
        ) : (
          <DataTable columns={columns} rows={expenses} rowKey={(e) => e.id} empty={{ title: t("archives.empty") }} />
        )}
      </Card>
    </>
  );
}

function PaymentsArchive() {
  const { t } = useTranslation();
  const { PAYMENT_STATUS, PAYMENT_TYPE } = useLabels();
  const { payments, isLoading, isError, refetch } = useArchivedPayments();
  const restore = useRestorePayment();

  const onRestore = async (id: string) => {
    try {
      await restore.mutateAsync(id);
      toast.success(t("archives.restoreSuccess"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("archives.restoreFailed")));
    }
  };

  const columns: Column<Payment>[] = [
    { key: "client", header: t("payments.columns.client"), cell: (p) => <span className="font-medium">{p.clientName}</span> },
    { key: "type", header: t("payments.columns.type"), cell: (p) => <Badge tone={PAYMENT_TYPE[p.type].tone}>{PAYMENT_TYPE[p.type].label}</Badge> },
    { key: "amount", header: t("payments.columns.amount"), align: "right", cell: (p) => <span className="mono">{formatDZD(p.amount)}</span> },
    { key: "status", header: t("payments.columns.status"), cell: (p) => <Badge tone={PAYMENT_STATUS[p.status].tone} dot>{PAYMENT_STATUS[p.status].label}</Badge> },
    { key: "date", header: t("payments.columns.date"), align: "right", cell: (p) => <span className="text-text3">{p.date}</span> },
    {
      key: "restore",
      header: "",
      align: "right",
      cell: (p) => (
        <Button variant="ghost" size="xs" onClick={() => onRestore(p.id)} disabled={restore.isPending}>
          <RotateCcw className="h-3 w-3 mr-1" /> {t("archives.restore")}
        </Button>
      ),
    },
  ];

  return (
    <>
      {isError && <ApiFallbackBanner onRetry={() => refetch()} />}
      <Card>
        {isLoading ? (
          <LoadingState rows={4} />
        ) : (
          <DataTable columns={columns} rows={payments} rowKey={(p) => p.id} empty={{ title: t("archives.empty") }} />
        )}
      </Card>
    </>
  );
}

function TasksArchive() {
  const { t } = useTranslation();
  const { TASK_STATUS, PRIORITY } = useLabels();
  const { tasks, isLoading, isError, refetch } = useArchivedTasks();
  const restore = useRestoreTask();

  const onRestore = async (id: string) => {
    try {
      await restore.mutateAsync(id);
      toast.success(t("archives.restoreSuccess"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("archives.restoreFailed")));
    }
  };

  const columns: Column<Task>[] = [
    { key: "title", header: t("tasks.columns.task"), cell: (tk) => <span className="font-medium">{tk.title}</span> },
    { key: "priority", header: t("tasks.columns.priority"), cell: (tk) => <Badge tone={PRIORITY[tk.priority].tone} dot>{PRIORITY[tk.priority].label}</Badge> },
    { key: "status", header: t("tasks.columns.status"), cell: (tk) => <Badge tone={TASK_STATUS[tk.status].tone}>{TASK_STATUS[tk.status].label}</Badge> },
    { key: "client", header: t("tasks.columns.client"), cell: (tk) => <span className="text-text3">{tk.clientName ?? "—"}</span> },
    {
      key: "restore",
      header: "",
      align: "right",
      cell: (tk) => (
        <Button variant="ghost" size="xs" onClick={() => onRestore(tk.id)} disabled={restore.isPending}>
          <RotateCcw className="h-3 w-3 mr-1" /> {t("archives.restore")}
        </Button>
      ),
    },
  ];

  return (
    <>
      {isError && <ApiFallbackBanner onRetry={() => refetch()} />}
      <Card>
        {isLoading ? (
          <LoadingState rows={4} />
        ) : (
          <DataTable columns={columns} rows={tasks} rowKey={(tk) => tk.id} empty={{ title: t("archives.empty") }} />
        )}
      </Card>
    </>
  );
}
